import React, { useEffect, useState } from "react";
import Button, {
  ButtonTypes,
  ButtonColorTypes,
} from "../shared-components/Button";
import InputField from "../shared-components/InputField";
import Divider from "../shared-components/LineDivider";
import { uniqueUUID } from "../utils";
import { PersonFormStateError } from "./PersonFormErrorState";
import PersonFormStringDate from "./PersonFormStringDate";

export interface JobDefinitions {
  title: string;
  company: string;
  startDate: string;
  endDate: string;
  key: string;
  country: string;
}

type Props = {
  onChange?: (aliases: JobDefinitions[]) => void;
  error?: PersonFormStateError;
  prefill?: JobDefinitions[];
  alternateColor?: boolean;
};

function buildEmptyDefinition(): JobDefinitions {
  return {
    title: "",
    company: "",
    startDate: "",
    endDate: "",
    country: "",
    key: uniqueUUID(),
  };
}

const PersonFormJob = ({ onChange, error, prefill, alternateColor }: Props) => {
  const [fieldsList, setFields] = useState<JobDefinitions[]>([]);
  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (
    idx: number,
    field: "title" | "company" | "startDate" | "endDate" | "country",
    value: string
  ) => {
    fieldsList[idx][field] = value;
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: JobDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    idx: number
  ) => {
    e.preventDefault();
    removeField(idx);
  };

  useEffect(() => {
    if (prefill && prefill.length >= 1) {
      handleChange([...prefill]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  return (
    <>
      {fieldsList.map((fields, idx) => (
        <div key={fields.key} className="flex flex-wrap">
          <InputField
            handleChange={(e) => {
              updateField(idx, "title", e.target.value);
            }}
            name={`title-${idx}`}
            value={fieldsList[idx].title}
            label={"Job Title"}
            classes="mb-4 mr-8"
            className={`${error?.jobs?.title?.[idx] ? "border-rose-600" : ""}`}
          />

          <InputField
            handleChange={(e) => {
              updateField(idx, "company", e.target.value);
            }}
            name={`company-${idx}`}
            placeholder=""
            value={fieldsList[idx].company}
            label={"Company Name"}
            classes={"mb-4 mr-8"}
            className={`${
              error?.jobs?.employer?.[idx] ? "border-rose-600" : ""
            }`}
          />
          <PersonFormStringDate
            index={idx}
            error={error}
            _name={`start-date-{idx}`}
            _label="Start Date"
            prefill={fieldsList[idx].startDate}
            onChange={(e) => {
              updateField(idx, "startDate", e);
            }}
          ></PersonFormStringDate>
          <PersonFormStringDate
            error={error}
            _name={`end-date-{idx}`}
            _label="End Date"
            index={idx}
            prefill={fieldsList[idx].endDate}
            onChange={(e) => {
              updateField(idx, "endDate", e);
            }}
          ></PersonFormStringDate>
          <InputField
            handleChange={(e) => {
              updateField(idx, "country", e.target.value);
            }}
            name={`country-${idx}`}
            placeholder=""
            label={"Country Name"}
            value={fieldsList[idx].country}
            classes={"mb-4 mr-8"}
          />
          {idx >= 0 && (
            <div className="w-full">
              <Button
                type={ButtonTypes.remove}
                text="Remove"
                colorType={ButtonColorTypes.negativeAction}
                onClick={(e) => handleRemove(e, idx)}
                className="mt-4 px-0"
              />
            </div>
          )}
          <Divider className="mb-6" />
        </div>
      ))}
      <Button
        type={ButtonTypes.add}
        text="Add Job"
        colorType={
          alternateColor ? ButtonColorTypes.alternate : ButtonColorTypes.action
        }
        onClick={handleAdd}
      />
    </>
  );
};

export default PersonFormJob;
